import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import exifr from 'exifr';

// WMO Wettercodes → Deutsch
function wetterBeschreibung(code) {
  if (code === 0) return 'Sonnig';
  if (code === 1) return 'Überwiegend klar';
  if (code === 2) return 'Teilweise bewölkt';
  if (code === 3) return 'Bedeckt';
  if ([45, 48].includes(code)) return 'Neblig';
  if ([51, 53, 55].includes(code)) return 'Nieselregen';
  if ([61, 63, 65].includes(code)) return 'Regen';
  if ([71, 73, 75].includes(code)) return 'Schneefall';
  if ([80, 81, 82].includes(code)) return 'Regenschauer';
  if ([95, 96, 99].includes(code)) return 'Gewitter';
  return 'Wechselhaft';
}

// Wetterwerte ins Formular übernehmen. Fehlende Werte bleiben leer –
// sonst stünde bei einem Teilausfall der Wetter-API "null°C" im Bericht.
const grad = v => (v == null ? '' : `${v}°C`);

function wetterFelder(w) {
  const temp = grad(w.temperatur);
  return {
    witterung_beginn: w.witterung, witterung_ende: w.witterung,
    temperatur_beginn: temp, temperatur_ende: temp,
    temp_min: grad(w.temp_min), temp_max: grad(w.temp_max),
  };
}

function addMinutes(timeStr, minutes) {
  if (!timeStr) timeStr = '00:00';
  const [h, m] = timeStr.split(':').map(Number);
  const total = ((h * 60 + m + minutes) % 1440 + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function ZeitBtn({ onClick, label, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-3)', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-2)' }}>
      {label}
    </button>
  );
}

function AutoSaveIndicator({ status }) {
  if (!status) return null;
  return <span className={`autosave ${status}`}>{status === 'saving' ? 'Speichert…' : '✓ Gespeichert'}</span>;
}

function Feld({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

// Personal-State aus DB-String laden
function parsePersonal(str) {
  try { return JSON.parse(str); } catch {}
  return { tiefbau: 0, kabel: 0, weitere: str || '' };
}

// Geräte-State aus DB-String laden
function parseGeraete(str) {
  try { return JSON.parse(str); } catch {}
  return { minibagger: false, lkw: false, transporter: false, radlader: false, spulbohr: false, verdichtung: false, kabelanhaenger: false, kompressor: false, felsmeissel: false, weitere: str || '' };
}

const TAETIGKEIT_LIST = [
  { key: 'abnahme', label: 'Abnahme' },
  { key: 'gemeinde', label: 'Abstimmung mit Gemeinde' },
  { key: 'aufmass', label: 'Aufmaßkontrolle' },
  { key: 'hausanschluesse', label: 'Aufnahme Hausanschlüsse' },
  { key: 'jourfixe', label: 'Jour Fixe' },
  { key: 'kontrolle', label: 'Kontrolle' },
  { key: 'mitverfolgung', label: 'Mitverfolgung' },
  { key: 'planung', label: 'Planung' },
  { key: 'rechnungen', label: 'Rechnungen' },
  { key: 'sonstiges', label: 'Sonstiges' },
  { key: 'baufirma', label: 'Treffen mit Baufirma' },
  { key: 'zwischenbericht', label: 'Zwischenbericht' },
];

function parseTaetigkeit(str) {
  try { return JSON.parse(str); } catch {}
  return {};
}

function TaetigkeitEingabe({ value, onChange, disabled }) {
  const t = parseTaetigkeit(value);
  const update = (key, val) => onChange(JSON.stringify({ ...t, [key]: val }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {TAETIGKEIT_LIST.map(({ key, label }) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: disabled ? 'default' : 'pointer', padding: '8px 14px', border: `1px solid ${t[key] ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8, background: t[key] ? 'var(--accent-dim)' : 'var(--bg-3)', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={!!t[key]} disabled={disabled} onChange={e => update(key, e.target.checked)}
              style={{ accentColor: 'var(--accent)', width: 16, height: 16 }} />
            {label}
          </label>
        ))}
      </div>
      <textarea className="form-textarea" placeholder="Weitere Anmerkungen…" disabled={disabled} rows={2}
        value={t.freitext || ''} onChange={e => update('freitext', e.target.value)}
        style={{ fontSize: '0.95rem', resize: 'vertical' }} />
    </div>
  );
}

function PersonalEingabe({ value, onChange, disabled }) {
  const p = parsePersonal(value);
  const update = (key, val) => onChange(JSON.stringify({ ...p, [key]: val }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {[
        { key: 'tiefbau', label: 'AK Tiefbau & Verlegung' },
        { key: 'kabel', label: 'AK Kabel & Montage' },
      ].map(({ key, label }) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ZeitBtn label="−" disabled={disabled || p[key] <= 0} onClick={() => update(key, Math.max(0, (p[key] || 0) - 1))} />
            <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 700, fontSize: '1rem' }}>{p[key] || 0}</span>
            <ZeitBtn label="+" disabled={disabled} onClick={() => update(key, (p[key] || 0) + 1)} />
          </div>
          <span style={{ fontSize: '0.875rem' }}>{label}</span>
        </div>
      ))}
      <input className="form-input" placeholder="Weitere (z.B. 1 Vorarbeiter)…" disabled={disabled}
        value={p.weitere || ''} onChange={e => update('weitere', e.target.value)} />
    </div>
  );
}

const GERAETE_LIST = [
  { key: 'minibagger', label: 'Minibagger' },
  { key: 'lkw', label: 'LKW' },
  { key: 'transporter', label: 'Transporter' },
  { key: 'radlader', label: 'Radlader' },
  { key: 'spulbohr', label: 'Spülbohrgerät(e)' },
];

const GERAETE_ZUSATZ_LIST = [
  { key: 'verdichtung', label: 'Verdichtungsgeräte' },
  { key: 'kabelanhaenger', label: 'Kabelanhänger' },
  { key: 'kompressor', label: 'Kompressor' },
  { key: 'felsmeissel', label: 'Felsmeissel f. Bagger' },
];

function GeraeteEingabe({ value, onChange, disabled }) {
  const g = parseGeraete(value);
  const [neuesGeraet, setNeuesGeraet] = useState('');
  const update = (key, val) => onChange(JSON.stringify({ ...g, [key]: val }));
  const items = (g.weitere || '').split(',').map(s => s.trim()).filter(Boolean);

  const addItem = (text) => {
    const trimmed = text.replace(/,/g, '').trim();
    if (!trimmed) return;
    const newItems = [...items, trimmed];
    onChange(JSON.stringify({ ...g, weitere: newItems.join(', ') }));
    setNeuesGeraet('');
  };

  const removeItem = (idx) => {
    const newItems = items.filter((_, i) => i !== idx);
    onChange(JSON.stringify({ ...g, weitere: newItems.join(', ') }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {GERAETE_LIST.map(({ key, label }) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: disabled ? 'default' : 'pointer', padding: '6px 12px', border: `1px solid ${g[key] ? 'var(--green)' : 'var(--border)'}`, borderRadius: 8, background: g[key] ? 'var(--green-dim)' : 'var(--bg-3)', fontSize: '0.875rem' }}>
            <input type="checkbox" checked={!!g[key]} disabled={disabled} onChange={e => update(key, e.target.checked)}
              style={{ accentColor: 'var(--green)', width: 15, height: 15 }} />
            {label}
          </label>
        ))}
        {GERAETE_ZUSATZ_LIST.map(({ key, label }) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: disabled ? 'default' : 'pointer', padding: '6px 12px', border: `1px solid ${g[key] ? 'var(--green)' : 'var(--border)'}`, borderRadius: 8, background: g[key] ? 'var(--green-dim)' : 'var(--bg-3)', fontSize: '0.875rem' }}>
            <input type="checkbox" checked={!!g[key]} disabled={disabled} onChange={e => update(key, e.target.checked)}
              style={{ accentColor: 'var(--green)', width: 15, height: 15 }} />
            {label}
          </label>
        ))}
        {items.map((item, idx) => (
          <button key={idx} disabled={disabled} onClick={() => !disabled && removeItem(idx)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid var(--green)', borderRadius: 8, background: 'var(--green-dim)', fontSize: '0.875rem', color: 'var(--text)', cursor: disabled ? 'default' : 'pointer', fontFamily: 'var(--font)' }}>
            <span style={{ color: 'var(--green)', fontSize: '0.8rem' }}>✓</span>
            {item}
            {!disabled && <span style={{ color: 'var(--text-3)', fontSize: '0.75rem', marginLeft: 2 }}>×</span>}
          </button>
        ))}
      </div>
      {!disabled && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="form-input" placeholder="Weiteres Gerät hinzufügen…"
            value={neuesGeraet}
            onChange={e => setNeuesGeraet(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addItem(neuesGeraet); } }} />
          <button className="btn btn-secondary" onClick={() => addItem(neuesGeraet)} disabled={!neuesGeraet.trim()}>+</button>
        </div>
      )}
    </div>
  );
}

function Lightbox({ src, beschriftung, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2000, cursor: 'zoom-out', padding: 20 }}>
      <AuthImage src={src} alt={beschriftung} style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }} onClick={e => e.stopPropagation()} />
      {beschriftung && <div style={{ color: '#fff', marginTop: 12, fontSize: '0.95rem', textAlign: 'center', maxWidth: 600 }}>{beschriftung}</div>}
      <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
    </div>
  );
}

function AuthImage({ src, alt, style, onClick }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('bau_token');
    let url = null;
    fetch(src, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => { url = URL.createObjectURL(blob); setBlobUrl(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [src]);
  if (!blobUrl) return <div style={{ ...style, background: 'var(--bg-3)', borderRadius: 6 }} />;
  return <img src={blobUrl} alt={alt} style={style} onClick={onClick} />;
}

function BildUpload({ berichtId, bilder, onBilderChange, disabled }) {
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();

  const handleFiles = async (files) => {
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('bild', file);
        const gps = await exifr.gps(file).catch(() => null);
        if (gps?.latitude != null && gps?.longitude != null) {
          fd.append('latitude', gps.latitude);
          fd.append('longitude', gps.longitude);
        }
        const result = await api.upload(`/bilder/${berichtId}`, fd);
        onBilderChange(prev => [...prev, result]);
      } catch (err) { alert('Upload fehlgeschlagen: ' + err.message); }
    }
    setUploading(false);
  };

  const handleBeschriftung = async (id, beschriftung) => {
    onBilderChange(prev => prev.map(b => b.id === id ? { ...b, beschriftung } : b));
    await api.put(`/bilder/${id}`, { beschriftung, reihenfolge: bilder.find(b => b.id === id)?.reihenfolge });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bild löschen?')) return;
    await api.delete(`/bilder/${id}`);
    onBilderChange(prev => prev.filter(b => b.id !== id));
  };

  return (
    <div>
      {lightbox && <Lightbox src={lightbox.src} beschriftung={lightbox.beschriftung} onClose={() => setLightbox(null)} />}
      {!disabled && (
        <>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => handleFiles(Array.from(e.target.files))} />
          <button type="button" className="btn btn-secondary" onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? <><span className="spinner" style={{ width: 15, height: 15 }} /> Lädt hoch…</> : '📷 Bilder hinzufügen'}
          </button>
        </>
      )}
      {bilder.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginTop: 14 }}>
          {bilder.map((b, i) => (
            <div key={b.id} className="card" style={{ padding: 8 }}>
              <AuthImage src={`/api/uploads/${b.dateiname}`} alt={b.beschriftung || `Bild ${i + 1}`}
                onClick={() => setLightbox({ src: `/api/uploads/${b.dateiname}`, beschriftung: b.beschriftung })}
                style={{ width: '100%', height: 130, objectFit: 'cover', borderRadius: 6, marginBottom: 6, cursor: 'zoom-in' }} />
              {!disabled ? (
                <>
                  <textarea className="form-input" placeholder="Beschriftung…" value={b.beschriftung || ''}
                    onChange={e => handleBeschriftung(b.id, e.target.value)} rows={2}
                    style={{ fontSize: '0.78rem', marginBottom: 6, resize: 'vertical' }} />
                  {(b.latitude && b.longitude) && (
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-3)', fontFamily: 'var(--font-mono)', marginBottom: 6 }}>
                      {parseFloat(b.latitude).toFixed(5)}, {parseFloat(b.longitude).toFixed(5)}
                    </div>
                  )}
                  <button type="button" className="btn btn-danger btn-sm" style={{ width: '100%' }} onClick={() => handleDelete(b.id)}>Löschen</button>
                </>
              ) : (
                b.beschriftung && <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>{b.beschriftung}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BerichtForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bericht, setBericht] = useState(null);
  const [bilder, setBilder] = useState([]);
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [form, setForm] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fertigLoading, setFertigLoading] = useState(false);
  const [wetterLoading, setWetterLoading] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    api.get(`/berichte/${id}`).then(data => {
      setBericht(data);
      setBilder(data.bilder || []);
      setTeilnehmer(data.teilnehmer || []);

      const now = new Date();
      const beginn = `${String(now.getHours() - 1).padStart(2, '0')}:00`;
      const ende = `${String(now.getHours() + 1).padStart(2, '0')}:00`;

      setForm({
        datum: data.datum?.slice(0, 10) || now.toISOString().slice(0, 10),
        arbeitsbeginn: data.arbeitsbeginn || beginn,
        arbeitsende: data.arbeitsende || ende,
        witterung_beginn: data.witterung_beginn || '',
        witterung_ende: data.witterung_ende || '',
        temperatur_beginn: data.temperatur_beginn || '',
        temperatur_ende: data.temperatur_ende || '',
        temp_min: data.temp_min || '',
        temp_max: data.temp_max || '',
        personaleinsatz: data.personaleinsatz || JSON.stringify({ tiefbau: 0, kabel: 0, weitere: '' }),
        geraeteeinsatz: data.geraeteeinsatz || JSON.stringify({ minibagger: false, lkw: false, transporter: false, radlader: false, spulbohr: false, weitere: '' }),
        baustoffanlieferung: data.baustoffanlieferung || '',
        bauleistungen: data.bauleistungen || '',
        sonstiges: data.sonstiges || '',
        personal_aktiv: data.personal_aktiv !== false,
        geraete_aktiv: data.geraete_aktiv !== false,
        baustoff_aktiv: data.baustoff_aktiv !== false,
        taetigkeit: data.taetigkeit || JSON.stringify({}),
        ebenfalls_anwesend: data.ebenfalls_anwesend || '',
        top1: data.top1 || '',
        top2: data.top2 || '',
        top3: data.top3 || '',
        top4: data.top4 || '',
        top5: data.top5 || '',
        naechster_termin: data.naechster_termin || '',
      });
      setLoading(false);

      if (data.typ === 'zwischenbericht' && !data.witterung_beginn && data.status !== 'fertig') {
        setWetterLoading(true);
        api.get(`/wetter/${data.projekt_id}`).then(w => {
          setForm(prev => {
            const next = { ...prev, ...wetterFelder(w) };
            saveTimer.current = setTimeout(() => save(next), 2000);
            return next;
          });
        }).catch(() => {}).finally(() => setWetterLoading(false));
      }
    }).catch(() => navigate('/'));
  }, [id, navigate]);

  const save = useCallback(async (data) => {
    setSaveStatus('saving');
    try {
      await api.put(`/berichte/${id}`, data);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch { setSaveStatus(null); }
  }, [id]);

  const set = (key, value) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => save(next), 1200);
      return next;
    });
  };

  const handleWetter = async () => {
    setWetterLoading(true);
    try {
      const w = await api.get(`/wetter/${bericht.projekt_id}`);
      setForm(prev => {
        const next = { ...prev, ...wetterFelder(w) };
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => save(next), 1200);
        return next;
      });
    } catch (e) {
      alert(e.message.includes('konfiguriert') ? 'Kein Wetterstandort für dieses Projekt konfiguriert.\nBitte im Admin-Bereich unter "Wetter" eintragen.' : 'Wetter konnte nicht geladen werden.');
    }
    setWetterLoading(false);
  };

  const handleTeilnehmer = async (tid, field, value) => {
    const aktuell = teilnehmer.find(t => t.id === tid) || {};
    const next = { anwesend: !!aktuell.anwesend, entschuldigt: !!aktuell.entschuldigt, [field]: value };
    setTeilnehmer(prev => prev.map(t => t.id === tid ? { ...t, [field]: value } : t));
    await api.put(`/berichte/${id}/teilnehmer/${tid}`, next);
  };

  const handleFertig = async () => {
    const confirmMsg = bericht.typ === 'arbeitszeit'
      ? 'Arbeitszeit erfassen?'
      : 'Bericht als fertig markieren? Eine E-Mail wird versendet.';
    if (!window.confirm(confirmMsg)) return;
    setFertigLoading(true);
    try {
      clearTimeout(saveTimer.current);
      await save(form);
      await api.post(`/berichte/${id}/fertig`, {});
      alert('Hat alles geklappt.');
      navigate('/');
    } catch (err) { alert('Fehler: ' + err.message); }
    finally { setFertigLoading(false); }
  };

  const handleDownload = async () => {
    const token = localStorage.getItem('bau_token');
    const res = await fetch(`/api/berichte/${id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : 'bericht.html';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const handleLoeschen = async () => {
    const msg = istFertig
      ? `Fertigen Bericht "${titel}" wirklich endgültig löschen? Das kann nicht rückgängig gemacht werden.`
      : `Entwurf "${titel}" löschen?`;
    if (!window.confirm(msg)) return;
    await api.delete(`/berichte/${id}`);
    navigate('/');
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  const istZwischenbericht = bericht.typ === 'zwischenbericht';
  const istArbeitszeit = bericht.typ === 'arbeitszeit';
  const istFertig = bericht.status === 'fertig';
  const typLabel = istZwischenbericht ? 'Zwischenbericht' : istArbeitszeit ? 'Arbeitszeit' : 'Jour Fixe';
  const titel = `${form.datum || bericht.datum?.slice(0, 10) || ''}_${bericht.projekt_name}_${typLabel}`;

  const arbeitsDauer = (() => {
    if (!form.arbeitsbeginn || !form.arbeitsende) return null;
    const [h1, m1] = form.arbeitsbeginn.split(':').map(Number);
    const [h2, m2] = form.arbeitsende.split(':').map(Number);
    const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (diff <= 0) return null;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return m === 0 ? `${h} Stunden` : `${h} Std. ${m} Min.`;
  })();

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: '0 16px', display: 'flex', alignItems: 'center', height: 52, gap: 10, position: 'sticky', top: 0, zIndex: 100 }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate('/', { state: { projekt_id: bericht?.projekt_id } })}>← Zurück</button>
        <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{titel}</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{bericht.projekt_name}</div>
        <span className={`badge badge-${bericht.status}`} style={{ marginLeft: 4 }}>{istFertig ? 'Fertig' : 'Entwurf'}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <AutoSaveIndicator status={saveStatus} />
          {!istFertig && !istArbeitszeit && (
            <button type="button" className="btn btn-sm" onClick={handleFertig} disabled={fertigLoading}
              style={{ background: 'var(--green)', color: '#fff', border: 'none' }}>
              {fertigLoading ? <span className="spinner" /> : '✓ Fertig markieren'}
            </button>
          )}
          {istFertig && !istArbeitszeit && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleDownload}>↓ Bericht herunterladen</button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ARBEITSZEIT – großes, klares Layout */}
        {istArbeitszeit && (
          <>
            <div className="card" style={{ padding: '28px 24px' }}>
              {/* Datum */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 28 }}>
                <ZeitBtn label="← Gestern" disabled={istFertig} onClick={() => set('datum', addDays(form.datum, -1))} />
                <input className="form-input" type="date" value={form.datum} onChange={e => set('datum', e.target.value)} disabled={istFertig}
                  style={{ flex: 1, maxWidth: 180, fontSize: '1.1rem', fontWeight: 700, textAlign: 'center' }} />
                <ZeitBtn label="Morgen →" disabled={istFertig} onClick={() => set('datum', addDays(form.datum, 1))} />
              </div>
              {/* Dauer-Anzeige */}
              {arbeitsDauer && (
                <div style={{ textAlign: 'center', marginBottom: 28 }}>
                  <div style={{ display: 'inline-block', background: 'var(--accent-dim)', border: '2px solid var(--accent)', borderRadius: 20, padding: '14px 36px' }}>
                    <div style={{ fontSize: '2.6rem', fontWeight: 800, color: 'var(--accent)', lineHeight: 1 }}>{arbeitsDauer}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 6, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Arbeitsdauer</div>
                  </div>
                </div>
              )}
              {/* Von / Bis */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--text-3)', marginBottom: 10, textTransform: 'uppercase' }}>Von</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ZeitBtn label="−30'" disabled={istFertig} onClick={() => set('arbeitsbeginn', addMinutes(form.arbeitsbeginn, -30))} />
                    <input className="form-input" type="time" value={form.arbeitsbeginn} onChange={e => set('arbeitsbeginn', e.target.value)} disabled={istFertig}
                      style={{ flex: 1, fontSize: '1.5rem', fontWeight: 700, textAlign: 'center', padding: '8px 2px' }} />
                    <ZeitBtn label="+30'" disabled={istFertig} onClick={() => set('arbeitsbeginn', addMinutes(form.arbeitsbeginn, 30))} />
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--text-3)', marginBottom: 10, textTransform: 'uppercase' }}>Bis</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ZeitBtn label="−30'" disabled={istFertig} onClick={() => set('arbeitsende', addMinutes(form.arbeitsende, -30))} />
                    <input className="form-input" type="time" value={form.arbeitsende} onChange={e => set('arbeitsende', e.target.value)} disabled={istFertig}
                      style={{ flex: 1, fontSize: '1.5rem', fontWeight: 700, textAlign: 'center', padding: '8px 2px' }} />
                    <ZeitBtn label="+30'" disabled={istFertig} onClick={() => set('arbeitsende', addMinutes(form.arbeitsende, 30))} />
                  </div>
                </div>
              </div>
            </div>
            <div className="card" style={{ padding: 24 }}>
              <h2 style={{ marginBottom: 18, fontSize: '1.1rem' }}>Was hast du heute gemacht?</h2>
              <TaetigkeitEingabe value={form.taetigkeit} onChange={v => set('taetigkeit', v)} disabled={istFertig} />
            </div>
          </>
        )}

        {/* DATUM + ZEITEN – Standard (Zwischenbericht / Jour Fixe) */}
        {!istArbeitszeit && (
          <div className="card" style={{ padding: 20 }}>
            <h2 style={{ marginBottom: 16 }}>Datum & Zeiten</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Feld label="Datum">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ZeitBtn label="← −1T" disabled={istFertig} onClick={() => set('datum', addDays(form.datum, -1))} />
                  <input className="form-input" type="date" value={form.datum} onChange={e => set('datum', e.target.value)} disabled={istFertig} style={{ flex: 1 }} />
                  <ZeitBtn label="+1T →" disabled={istFertig} onClick={() => set('datum', addDays(form.datum, 1))} />
                </div>
              </Feld>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Feld label="Arbeitsbeginn">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ZeitBtn label="−30'" disabled={istFertig} onClick={() => set('arbeitsbeginn', addMinutes(form.arbeitsbeginn, -30))} />
                    <input className="form-input" type="time" value={form.arbeitsbeginn} onChange={e => set('arbeitsbeginn', e.target.value)} disabled={istFertig} style={{ flex: 1 }} />
                    <ZeitBtn label="+30'" disabled={istFertig} onClick={() => set('arbeitsbeginn', addMinutes(form.arbeitsbeginn, 30))} />
                  </div>
                </Feld>
                <Feld label="Arbeitsende">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ZeitBtn label="−30'" disabled={istFertig} onClick={() => set('arbeitsende', addMinutes(form.arbeitsende, -30))} />
                    <input className="form-input" type="time" value={form.arbeitsende} onChange={e => set('arbeitsende', e.target.value)} disabled={istFertig} style={{ flex: 1 }} />
                    <ZeitBtn label="+30'" disabled={istFertig} onClick={() => set('arbeitsende', addMinutes(form.arbeitsende, 30))} />
                  </div>
                </Feld>
              </div>
            </div>
          </div>
        )}

        {/* ZWISCHENBERICHT-FELDER */}
        {istZwischenbericht && (
          <>
            {/* WETTER */}
            <div className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2>Wetter</h2>
                {!istFertig && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleWetter} disabled={wetterLoading}>
                    {wetterLoading ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Lädt…</> : '🌤 Aktuell laden'}
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Feld label="Witterung">
                  <input className="form-input" placeholder="z.B. leicht bewölkt" value={form.witterung_beginn} onChange={e => set('witterung_beginn', e.target.value)} disabled={istFertig} />
                </Feld>
                <Feld label="Temperatur">
                  <input className="form-input" placeholder="z.B. 10°C" value={form.temperatur_beginn} onChange={e => set('temperatur_beginn', e.target.value)} disabled={istFertig} />
                </Feld>
                <Feld label="Tagesminimum">
                  <input className="form-input" placeholder="z.B. 5°C" value={form.temp_min} onChange={e => set('temp_min', e.target.value)} disabled={istFertig} />
                </Feld>
                <Feld label="Tagesmaximum">
                  <input className="form-input" placeholder="z.B. 15°C" value={form.temp_max} onChange={e => set('temp_max', e.target.value)} disabled={istFertig} />
                </Feld>
              </div>
            </div>

            {/* PERSONAL */}
            <div className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: form.personal_aktiv ? 16 : 0 }}>
                <h2 style={{ color: form.personal_aktiv ? undefined : 'var(--text-3)', textDecoration: form.personal_aktiv ? 'none' : 'line-through' }}>Personaleinsatz</h2>
                {!istFertig && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => set('personal_aktiv', !form.personal_aktiv)}>
                    {form.personal_aktiv ? 'nicht heute' : '↩ einblenden'}
                  </button>
                )}
              </div>
              {form.personal_aktiv && <PersonalEingabe value={form.personaleinsatz} onChange={v => set('personaleinsatz', v)} disabled={istFertig} />}
            </div>

            {/* GERÄTE */}
            <div className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: form.geraete_aktiv ? 16 : 0 }}>
                <h2 style={{ color: form.geraete_aktiv ? undefined : 'var(--text-3)', textDecoration: form.geraete_aktiv ? 'none' : 'line-through' }}>Geräte- und Fahrzeugeinsatz</h2>
                {!istFertig && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => set('geraete_aktiv', !form.geraete_aktiv)}>
                    {form.geraete_aktiv ? 'nicht heute' : '↩ einblenden'}
                  </button>
                )}
              </div>
              {form.geraete_aktiv && <GeraeteEingabe value={form.geraeteeinsatz} onChange={v => set('geraeteeinsatz', v)} disabled={istFertig} />}
            </div>

            {/* BAUSTOFFANLIEFERUNG */}
            <div className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: form.baustoff_aktiv ? 16 : 0 }}>
                <h2 style={{ color: form.baustoff_aktiv ? undefined : 'var(--text-3)', textDecoration: form.baustoff_aktiv ? 'none' : 'line-through' }}>Baustoffanlieferung</h2>
                {!istFertig && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => set('baustoff_aktiv', !form.baustoff_aktiv)}>
                    {form.baustoff_aktiv ? 'nicht heute' : '↩ einblenden'}
                  </button>
                )}
              </div>
              {form.baustoff_aktiv && <textarea className="form-textarea" placeholder="Angelieferte Materialien…" value={form.baustoffanlieferung} onChange={e => set('baustoffanlieferung', e.target.value)} disabled={istFertig} />}
            </div>

            {/* ZWISCHENBERICHT */}
            <div className="card" style={{ padding: 20 }}>
              <h2 style={{ marginBottom: 16 }}>Zwischenbericht</h2>
              <textarea className="form-textarea" style={{ minHeight: 220 }}
                placeholder="Bauleistungen, aktueller Stand, Besonderheiten, Sonstiges…"
                value={form.bauleistungen} onChange={e => set('bauleistungen', e.target.value)} disabled={istFertig} />
            </div>
          </>
        )}

        {/* JOUR FIXE */}
        {!istZwischenbericht && !istArbeitszeit && (
          <>
            {teilnehmer.length > 0 && (
              <div className="card" style={{ padding: 20 }}>
                <h2 style={{ marginBottom: 14 }}>Teilnehmer</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {teilnehmer.map(t => (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ flex: 1, fontSize: '0.9rem' }}>{t.name}</span>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: istFertig ? 'default' : 'pointer', fontSize: '0.8rem', color: 'var(--text-2)' }}>
                        <input type="checkbox" checked={!!t.anwesend} disabled={istFertig}
                          onChange={e => handleTeilnehmer(t.id, 'anwesend', e.target.checked)}
                          style={{ width: 18, height: 18, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                        anwesend
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: istFertig ? 'default' : 'pointer', fontSize: '0.8rem', color: 'var(--text-2)' }}>
                        <input type="checkbox" checked={!!t.entschuldigt} disabled={istFertig}
                          onChange={e => handleTeilnehmer(t.id, 'entschuldigt', e.target.checked)}
                          style={{ width: 18, height: 18, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                        entschuldigt
                      </label>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 14 }}>
                  <Feld label="Ebenfalls anwesend">
                    <input className="form-input" placeholder="Weitere Teilnehmer, kommagetrennt…"
                      value={form.ebenfalls_anwesend} onChange={e => set('ebenfalls_anwesend', e.target.value)} disabled={istFertig} />
                  </Feld>
                </div>
              </div>
            )}
            {[
              { key: 'top1', label: 'TOP 1 · Aktueller Baufortschritt' },
              { key: 'top2', label: 'TOP 2 · Weiterer Bauablauf' },
              { key: 'top3', label: 'TOP 3 · Trassenänderungen' },
              { key: 'top4', label: 'TOP 4 · Hausanschlussänderungen' },
              { key: 'top5', label: 'TOP 5 · Sonstiges' },
            ].map(({ key, label }) => (
              <div key={key} className="card" style={{ padding: 20 }}>
                <Feld label={label}>
                  <textarea className="form-textarea" style={{ minHeight: 140 }} value={form[key]} onChange={e => set(key, e.target.value)} disabled={istFertig} />
                </Feld>
              </div>
            ))}
            <div className="card" style={{ padding: 20 }}>
              <Feld label="TOP 6 · Nächster Termin">
                <input className="form-input" placeholder="z.B. Dienstag, 27.05.2026, 9:00 Uhr, Baustelle POP-Gebäude…"
                  value={form.naechster_termin} onChange={e => set('naechster_termin', e.target.value)} disabled={istFertig} />
              </Feld>
            </div>
          </>
        )}

        {/* BILDER */}
        {!istArbeitszeit && (
          <div className="card" style={{ padding: 20 }}>
            <h2 style={{ marginBottom: 16 }}>Bilddokumentation</h2>
            <BildUpload berichtId={id} bilder={bilder} onBilderChange={setBilder} disabled={istFertig} />
          </div>
        )}

        {istArbeitszeit ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-danger" onClick={handleLoeschen}
              style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: '1.1rem' }}>🗑</span>
              {istFertig ? 'Eintrag löschen' : 'Verwerfen'}
            </button>
            {!istFertig && (
              <button type="button" className="btn" onClick={handleFertig} disabled={fertigLoading}
                style={{ background: 'var(--green)', color: '#fff', border: 'none', padding: '10px 28px', fontSize: '1rem', fontWeight: 700, borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                {fertigLoading ? <span className="spinner" /> : <><span style={{ fontSize: '1.1rem' }}>✓</span> Speichern</>}
              </button>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'right' }}>
            <button type="button" className="btn btn-danger btn-sm" onClick={handleLoeschen}>
              {istFertig ? 'Bericht endgültig löschen' : 'Entwurf löschen'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
